import React, { useMemo } from 'react'
import { NavLink, Navigate, Outlet, useOutletContext } from 'react-router-dom'
import { useRedEnvelopeUserEntry } from '../../hooks/useRedEnvelopeUserEntry'
import { useLuckyWheelUserEntry } from '../../hooks/useLuckyWheelUserEntry'
import { useBerThongUserEntry } from '../../hooks/useBerThongUserEntry'
import { useScratchLotteryUserEntry } from '../../hooks/useScratchLotteryUserEntry'
import { useHuayJackpotUserEntry } from '../../hooks/useHuayJackpotUserEntry'
import type { HistoryOutletContext } from './ModernHistoryLayout'

type GameHistoryTab = {
  to: string
  label: string
  fa: string
  requireRedEnvelope?: boolean
  requireLuckyWheel?: boolean
  requireBerThong?: boolean
  requireScratchLottery?: boolean
  requireHuayJackpot?: boolean
  soon?: boolean
}

const GAME_TABS: GameHistoryTab[] = [
  { to: 'btd', label: 'เกมส์ BTD', fa: 'fas fa-gamepad' },
  { to: 'red-envelope', label: 'เกมส์ซองแดง', fa: 'fas fa-envelope', requireRedEnvelope: true },
  { to: 'scratch-lottery', label: 'หวยขูดเมกา', fa: 'fas fa-ticket-alt', requireScratchLottery: true },
  { to: 'huay-jackpot', label: 'หวยแจ็กพอต', fa: 'fas fa-bullseye', requireHuayJackpot: true },
  { to: 'lucky-wheel', label: 'วงล้อมหาโชค', fa: 'fas fa-dharmachakra', requireLuckyWheel: true },
  { to: 'ber-thong', label: 'เบอร์ทอง', fa: 'fas fa-th', requireBerThong: true },
]

function useVisibleGameTabs(parent: HistoryOutletContext) {
  const { showRedEnvelopeEntry } = useRedEnvelopeUserEntry(parent.themeConfig.settings.redEnvelopeEnabled)
  const { showLuckyWheelEntry } = useLuckyWheelUserEntry(parent.themeConfig.settings.luckyWheelEnabled)
  const { showBerThongEntry } = useBerThongUserEntry(parent.themeConfig.settings.berThongEnabled)
  const { showScratchLotteryEntry } = useScratchLotteryUserEntry(parent.themeConfig.settings.scratchLotteryEnabled)
  const { showHuayJackpotEntry } = useHuayJackpotUserEntry(parent.themeConfig.settings.huayJackpotEnabled)

  return useMemo(
    () =>
      GAME_TABS.filter((tab) => {
        if (tab.requireRedEnvelope && !showRedEnvelopeEntry) return false
        if (tab.requireLuckyWheel && !showLuckyWheelEntry) return false
        if (tab.requireBerThong && !showBerThongEntry) return false
        if (tab.requireScratchLottery && !showScratchLotteryEntry) return false
        if (tab.requireHuayJackpot && !showHuayJackpotEntry) return false
        return true
      }),
    [showRedEnvelopeEntry, showLuckyWheelEntry, showBerThongEntry, showScratchLotteryEntry, showHuayJackpotEntry],
  )
}

export function HistoryGamesIndexRedirect() {
  const parent = useOutletContext<HistoryOutletContext>()
  const tabs = useVisibleGameTabs(parent)
  return <Navigate to={tabs[0]?.to || 'btd'} replace />
}

/**
 * กลุ่มประวัติเกมส์ — แท็บหลักอันเดียว แล้วแยกเกมเป็นซับแท็บ
 * (มือถือ: เลื่อนแนวนอนได้)
 */
const HistoryGamesLayout: React.FC = () => {
  const parent = useOutletContext<HistoryOutletContext>()
  const tabs = useVisibleGameTabs(parent)

  return (
    <div className="htd-hist__games">
      <div className="htd-hist__games-head">
        <h2 className="htd-hist__page-title">ประวัติเกมส์</h2>
        <p className="htd-hist__games-desc">เลือกเกมเพื่อดูประวัติการเล่น</p>
      </div>

      <nav className="htd-hist__game-tabs" aria-label="แท็บประวัติเกมส์">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              `htd-hist__game-tab${isActive ? ' is-active' : ''}${tab.soon ? ' is-soon' : ''}`
            }
          >
            <span className="htd-hist__game-tab-icon" aria-hidden>
              <i className={tab.fa} />
            </span>
            <span className="htd-hist__game-tab-label">{tab.label}</span>
            {tab.soon ? <span className="htd-hist__game-tab-soon">เร็วๆ นี้</span> : null}
          </NavLink>
        ))}
      </nav>

      <div className="htd-hist__games-outlet">
        <Outlet context={parent} />
      </div>
    </div>
  )
}

export default HistoryGamesLayout
